import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Bell, BellOff, Check, CheckCheck, X,
  GraduationCap, ClipboardList, AlertCircle,
  ChevronDown, Smartphone
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import api from '../services/api';
import usePushNotifications from '../hooks/usePushNotifications';

const NotificationsInApp = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    isLoading: pushLoading,
    subscribe: pushSubscribe,
    unsubscribe: pushUnsubscribe,
  } = usePushNotifications();

  useEffect(() => {
    loadNotifications();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count')
      ]);
      setNotifications(notifRes.data || []);
      setUnreadCount(countRes.data?.count || 0);
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data?.count || 0);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, lu: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, lu: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'diagnostic_valide':
      case 'diagnostic_validation':
        return <GraduationCap className="w-5 h-5 text-blue-600" />;
      case 'competence_ajoutee':
      case 'competence_approuvee':
        return <ClipboardList className="w-5 h-5 text-emerald-600" />;
      case 'competence_refusee':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Bell className="w-5 h-5 text-slate-600" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `Il y a ${Math.floor(diff / 86400000)}j`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="relative rounded-xl"
          data-testid="notifications-button"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center rounded-full"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="w-80 max-h-[400px] overflow-y-auto rounded-xl p-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50 sticky top-0">
          <span className="font-semibold text-slate-900">Notifications</span>
          <div className="flex items-center gap-1">
            {/* Bouton push smartphone */}
            {pushSupported && (
              <Button
                variant="ghost"
                size="sm"
                onClick={pushSubscribed ? pushUnsubscribe : pushSubscribe}
                disabled={pushLoading}
                title={pushSubscribed ? 'Désactiver les alertes smartphone' : 'Activer les alertes smartphone'}
                className={`text-xs ${pushSubscribed ? 'text-green-600 hover:text-red-500' : 'text-slate-400 hover:text-blue-600'}`}
              >
                {pushLoading ? (
                  <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                ) : pushSubscribed ? (
                  <><Smartphone className="w-3 h-3 mr-1" /><span className="hidden sm:inline">Alertes ON</span></>
                ) : (
                  <><BellOff className="w-3 h-3 mr-1" /><span className="hidden sm:inline">Alertes OFF</span></>
                )}
              </Button>
            )}
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                Tout lu
              </Button>
            )}
          </div>
        </div>

        {/* Liste des notifications */}
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-6 text-center text-slate-500">
            <Bell className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">Aucune notification</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {notifications.map(notif => (
              <div 
                key={notif.id}
                className={`p-3 hover:bg-slate-50 transition-colors cursor-pointer ${
                  !notif.lu ? 'bg-blue-50' : ''
                }`}
                onClick={() => !notif.lu && markAsRead(notif.id)}
                data-testid={`notification-${notif.id}`}
              >
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getIcon(notif.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm ${!notif.lu ? 'font-semibold' : 'font-medium'} text-slate-900`}>
                        {notif.titre}
                      </p>
                      {!notif.lu && (
                        <span className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-1.5"></span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">
                      {notif.message}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatDate(notif.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsInApp;
